// GtCandidateLexa: the Lexa tab on a candidate's record. Admins and super
// admins only (the server refuses anyone else too).
//
// Every coaching session they have had, with video, report and transcript;
// Lexa's memory note about them; their credits and what they have paid.
// None of this is ever shown or sent to a client.
import { useState, useEffect, useCallback } from 'react';
import { useAuthFetch, SessionModal, money } from '../screens/gt/GtLexaAdmin';
import { ukTime, ukDate, mins, statusOf } from './GtLexaSession';

const REASON = { free: 'First session free', purchase: 'Bought', session: 'Used', returned: 'Returned', refund: 'Refunded', grant: 'Given' };

function ledgerLine(x) {
  const label = REASON[x.reason] || x.reason;
  if (x.reason === 'purchase' || x.reason === 'refund') return label + (x.currency ? ' ' + money(x.currency, x.amount_minor) : '');
  if (x.reason === 'free') return label;
  const note = String(x.note || '').replace(/^Returned: /, '');
  return note ? label + ': ' + note : label;
}

export default function GtCandidateLexa({ candidateId, getToken }) {
  const authFetch = useAuthFetch(getToken);
  const [sessions, setSessions] = useState(null);
  const [memory, setMemory] = useState(null);
  const [credits, setCredits] = useState(null);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState('');
  const [viewId, setViewId] = useState(null);
  const [toast, setToast] = useState(null);
  const flash = useCallback((m) => { setToast(m); setTimeout(() => setToast(null), 2800); }, []);

  const load = useCallback(async () => {
    if (!candidateId) return;
    try {
      const [s, m, c, p] = await Promise.all([
        authFetch('/api/lexa/sessions?filter=real&candidate_id=' + candidateId),
        authFetch('/api/lexa/memory/' + candidateId),
        authFetch('/api/lexa/candidates/' + candidateId + '/credits'),
        authFetch('/api/lexa/payments'),
      ]);
      setSessions(s.sessions || []); setMemory(m.memory || null); setCredits(c);
      setPayments((p.payments || []).filter((x) => x.candidate_id === Number(candidateId) && x.status !== 'pending'));
      setError('');
    } catch (e) { setError(e.message); }
  }, [authFetch, candidateId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="gtiv-card gtiv-err">Could not load Lexa: {error} <button type="button" className="gtiv-link" onClick={load}>Try again</button></div>;
  if (!sessions || !credits) return <div className="gtiv-card gtiv-loading">Loading Lexa…</div>;

  const done = sessions.filter((s) => s.status === 'completed').length;
  const paid = payments.filter((p) => p.status === 'paid' || p.status === 'part_refunded');

  return (
    <div className="gtclx">
      <section className="gtiv-card gtclx-head">
        <span className="gtlx-orb" aria-hidden="true" />
        <div className="gtclx-head-t">
          <b>Coaching with Lexa</b>
          <span>
            {sessions.length ? done + (done === 1 ? ' session' : ' sessions') + ' finished' : 'No sessions yet'}
            {' · '}{credits.credits === 1 ? '1 credit left' : credits.credits + ' credits left'}
            {paid.length ? ' · paid ' + paid.map((p) => money(p.currency, p.amount_minor - (p.refunded_minor || 0))).join(', ') : ''}
          </span>
        </div>
        <span className="gtclx-private">Never shown to clients</span>
      </section>

      <section className="gtiv-card gtclx-mem">
        <span className="gtiv-k">Lexa's memory of them</span>
        <p>{memory && memory.note ? memory.note : 'Nothing yet. Lexa writes this after their first full session and reads it before every one after.'}</p>
        {memory && memory.updated_at && <span className="gtclx-fine">Updated {ukTime(memory.updated_at)}{memory.session_count ? ' · from ' + memory.session_count + (memory.session_count === 1 ? ' session' : ' sessions') : ''}</span>}
      </section>

      {sessions.length > 0 && (
        <div className="gtivl-list">
          {sessions.map((s) => {
            const st = statusOf(s);
            return (
              <button key={s.id} type="button" className="gtiv-card gtivl-row gtlx-row" onClick={() => setViewId(s.id)}>
                <span className={'gtptlx-row-ic gtptlx-row-ic--' + s.kind} aria-hidden="true">{s.kind === 'interview' ? 'IV' : 'CC'}</span>
                <span className="gtivl-row-t">
                  <b>{s.title}</b>
                  <span>
                    {ukTime(s.started_at || s.created_at)}
                    {s.duration_seconds ? ' · ' + mins(s.duration_seconds) : ''}
                    {s.kind === 'interview' ? ' · ' + (s.mode === 'real' ? 'Real mode' : 'Practice mode') : ''}
                  </span>
                </span>
                <span className={'gtlx-mtag gtlx-mtag--' + s.model}>{s.model === 'sol' ? 'Sol' : 'Haiku'}{s.model_ran && s.model_ran.fell_back ? ' → Haiku' : ''}</span>
                {s.recordings > 0 && <span className="gtivl-vid"><svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>Video</span>}
                <span className={'gtivl-pill gtivl-pill--' + st.tone}>{st.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {credits.ledger.length > 0 && (
        <section className="gtiv-card gtclx-ledger">
          <span className="gtiv-k">Credits</span>
          {credits.ledger.map((x, i) => (
            <div key={i} className="gtclx-lrow">
              <span className={'gtclx-delta' + (x.delta > 0 ? ' gtclx-delta--plus' : '')}>{x.delta > 0 ? '+' + x.delta : x.delta}</span>
              <span className="gtclx-lwhat">{ledgerLine(x)}</span>
              <span className="gtclx-lwhen">{ukDate(x.created_at)}</span>
            </div>
          ))}
        </section>
      )}

      {viewId && <SessionModal id={viewId} authFetch={authFetch} getToken={getToken} flash={flash} onClose={() => setViewId(null)} onChanged={load} />}
      {toast && <div className="gtivl-toast" role="status">{toast}</div>}
    </div>
  );
}
